import { createFileRoute } from '@tanstack/react-router'
import { Container } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import {
  FileText,
  Layers,
  PenTool,
  Phone,
  ArrowRight,
  Award,
  Clock,
  BookOpen,
  CheckCircle,
  Hexagon,
  Printer,
  Stamp,
  FileCheck,
  Copyright
} from 'lucide-react'
import { generateWhatsAppLink } from '@/lib/utils/whatsapp'

// Tabel Layanan Pracetak - Style seperti gambar referensi
const pracetakTableData = [
  {
    no: 1,
    layanan: 'Desain Cover',
    harga: 'Rp 250.000,-',
    fasilitas: '1 kali Revisi',
    icon: PenTool,
    detail: 'Desain profesional dengan konsep menarik'
  },
  {
    no: 2,
    layanan: 'Layout / Tata Letak',
    harga: 'Rp 4.000,-',
    fasilitas: 'Per Halaman',
    icon: Layers,
    detail: 'Typesetting standar penerbitan ISBN'
  },
  {
    no: 3,
    layanan: 'ISBN & Barcode',
    harga: 'Rp 300.000,-',
    fasilitas: 'Termasuk deposit perpusnas',
    icon: Stamp,
    detail: 'Pengurusan resmi Perpusnas RI'
  },
  {
    no: 4,
    layanan: 'Edit & Proofreading',
    harga: 'Rp 4.000,-',
    fasilitas: 'Per Halaman',
    icon: FileCheck,
    detail: 'Penyuntingan bahasa dan tata bahasa'
  },
  {
    no: 5,
    layanan: 'HKI (Hak Cipta)',
    harga: 'Rp 350.000,-',
    fasilitas: 'Sertifikat resmi Kemenkumham',
    icon: Copyright,
    detail: 'Perlindungan hukum karya intelektual'
  }
]


const steps = [
  {
    title: 'Konsultasi Awal',
    description: 'Diskusikan kebutuhan pra cetak Anda dengan tim kami. Kami akan memberikan rekomendasi layanan yang sesuai.'
  },
  {
    title: 'Kirim Naskah',
    description: 'Kirimkan naskah dalam format Word atau PDF. Tim kami akan mereview dan memberikan estimasi waktu pengerjaan.'
  },
  {
    title: 'Proses Pengerjaan',
    description: 'Tim profesional kami mengerjakan layout, desain, dan editing sesuai standar penerbitan.'
  },
  {
    title: 'Review & Finalisasi',
    description: 'Anda mereview hasil kerja dan memberikan feedback. Setelah disetujui, file siap cetak akan dikirimkan.'
  }
]

const benefits = [
  {
    icon: Award,
    title: 'Tim Profesional',
    description: 'Dikerjakan oleh desainer dan editor berpengalaman di bidang penerbitan.'
  },
  {
    icon: Clock,
    title: 'Pengerjaan Cepat',
    description: 'Proses pengerjaan efisien dengan timeline yang jelas dan terukur.'
  },
  {
    icon: BookOpen,
    title: 'Standar Penerbitan',
    description: 'Hasil sesuai standar penerbitan nasional dan siap cetak.'
  }
]

export const Route = createFileRoute('/pra-cetak/')({
  component: PraCetak,
  head: () => ({
    title: 'Pra Cetak - Layout, Desain Cover & Editing - Pustaka Pranala',
    meta: [
      {
        name: 'description',
        content: 'Layanan pra cetak profesional: layout buku, desain cover, editing, proofreading, dan pengurusan ISBN. Siapkan naskah Anda untuk dicetak.',
      },
    ],
  }),
})

function PraCetak() {
  const handleServiceOrder = (layanan: string) => {
    const message = `Halo, saya tertarik dengan layanan ${layanan} dari Pranala Pracetak. Mohon info lebih lanjut. Terima kasih!`
    const whatsappUrl = generateWhatsAppLink(message)
    window.open(whatsappUrl, '_blank')
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-slate-800 py-16 lg:py-20">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-yellow-500 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-neutral-700 rounded-full blur-3xl" />
        </div>
        <Container className="relative z-10">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-yellow-300 text-xs font-medium tracking-wide mb-6">
              <FileText className="w-4 h-4" />
              PRA CETAK
            </div>
            <h1 className="text-4xl md:text-5xl text-white tracking-tight mb-6">
              Layanan Pra Cetak
              <span className="text-yellow-400"> Profesional</span>
            </h1>
            <p className="text-lg text-neutral-400 mb-8 max-w-2xl mx-auto">
              Persiapkan naskah Anda untuk dicetak dengan layanan pra cetak lengkap.
              Dari layout, desain cover, editing, hingga pengurusan ISBN.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
              <div>
                <div className="text-3xl font-bold text-yellow-400">500+</div>
                <div className="text-sm text-neutral-400">Naskah Dikerjakan</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-400">5-10</div>
                <div className="text-sm text-neutral-400">Hari Kerja</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-400">100%</div>
                <div className="text-sm text-neutral-400">Standar ISBN</div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-16 max-w-7xl mx-auto">
        {/* NEW: Pranala Pracetak Table Card - Desain seperti referensi */}
        <div className="mb-20">
          <div className="max-w-7xl mx-auto">
            {/* Card Container dengan gradient dark blue */}
            <div className="relative overflow-hidden rounded-2xl bg-linear-to-br from-slate-800 via-slate-700 to-slate-900 shadow-2xl">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />

              <div className="relative p-8 lg:p-12">
                {/* Header dengan Icon Hexagon */}
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-10">
                  <div className="relative">
                    <Hexagon className="w-20 h-20 text-amber-500 fill-amber-500" strokeWidth={1.5} />
                    <Printer className="w-8 h-8 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                      Pracetak
                    </h2>
                    <p className="text-slate-300 text-lg">
                      Memenuhi kebutuhan spesifik persiapan naskah sebelum cetak massal
                    </p>
                  </div>
                </div>

                {/* Tabel Layanan */}
                <div className="overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                  {/* Header Tabel */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 p-4 bg-yellow-500/20 border-b border-white/10 text-sm font-semibold text-yellow-300 uppercase tracking-wider">
                    <div className="col-span-1 text-center">No</div>
                    <div className="col-span-4">Layanan</div>
                    <div className="col-span-3">Harga</div>
                    <div className="col-span-4">Fasilitas dan Layanan</div>
                  </div>

                  {/* Body Tabel */}
                  <div className="divide-y divide-white/10">
                    {pracetakTableData.map((item) => (
                      <div
                        key={item.no}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 items-center hover:bg-white/5 transition-colors group cursor-pointer"
                        onClick={() => handleServiceOrder(item.layanan)}
                      >
                        {/* No & Icon */}
                        <div className="md:col-span-1 flex items-center gap-3 md:justify-center">
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-400 font-bold text-sm">
                            {item.no}
                          </span>
                        </div>

                        {/* Layanan */}
                        <div className="md:col-span-4">
                          <div className="flex items-center gap-3">
                            <item.icon className="w-5 h-5 text-yellow-400 md:hidden" />
                            <div>
                              <h4 className="text-white font-semibold group-hover:text-yellow-400 transition-colors">
                                {item.layanan}
                              </h4>
                              <p className="text-slate-400 text-sm md:hidden">{item.detail}</p>
                            </div>
                          </div>
                        </div>

                        {/* Harga */}
                        <div className="md:col-span-3">
                          <span className="text-xl font-bold text-white font-mono">
                            {item.harga}
                          </span>
                        </div>

                        {/* Fasilitas */}
                        <div className="md:col-span-4">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                            <span className="text-slate-300 text-sm">{item.fasilitas}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA di dalam card */}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-white/10">
                  <p className="text-slate-400 text-sm">
                    *Harga dapat berubah sesuai kompleksitas dan volume naskah
                  </p>
                  <Button
                    onClick={() => handleServiceOrder('Paket Pracetak')}
                    className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-bold px-8 py-3 rounded-lg border-0 shadow-lg shadow-yellow-500/25 transition-all hover:shadow-yellow-500/40 cursor-pointer"
                  >
                    Pesan Layanan Pracetak
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <section className="py-24 px-6 bg-neutral-50 -mx-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              {/* Left Column - Heading */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                  Dari naskah mentah<br />ke file siap cetak.
                </h2>
                <p className="text-neutral-500 mb-8">
                  Proses pra cetak yang terstruktur memastikan naskah Anda siap untuk dicetak dengan kualitas terbaik.
                </p>

                <a
                  href="/kontak"
                  className="inline-flex items-center gap-2 text-sm font-medium text-neutral-900 hover:gap-3 transition-all group"
                >
                  Mulai Konsultasi
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Right Column - Steps */}
              <div className="space-y-0">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex gap-6 py-6 border-b border-neutral-100 group last:border-b-0"
                  >
                    <span className="text-xs font-mono text-neutral-400 pt-1">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h4 className="text-lg font-medium text-neutral-900 mb-2 group-hover:text-yellow-600 transition-colors">
                        {step.title}
                      </h4>
                      <p className="text-sm text-neutral-500 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <div className="mb-16 mt-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-neutral-900 tracking-tight mb-4">
              Mengapa Pra Cetak Bersama Kami?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-yellow-600" />
                </div>
                <h3 className="text-lg text-neutral-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-neutral-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative overflow-hidden rounded-2xl bg-neutral-900 p-8 lg:p-12">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-12 -right-12 w-64 h-64 bg-yellow-500 rounded-full blur-3xl" />
          </div>
          <div className="relative z-10 text-center">
            <h3 className="text-2xl lg:text-3xl text-white mb-4">
              Siap Mempersiapkan Naskah Anda?
            </h3>
            <p className="text-neutral-400 mb-8 max-w-xl mx-auto">
              Dapatkan konsultasi gratis dan penawaran terbaik untuk layanan pra cetak buku Anda
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-yellow-500 hover:bg-yellow-400 text-white font-medium rounded-lg text-sm border-0 cursor-pointer"
              >
                <a href="/kontak" className="flex items-center justify-center">
                  <Phone className="w-4 h-4 mr-2" />
                  Konsultasi Gratis
                </a>
              </Button>
              <Button
                variant="outline"
                asChild
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium rounded-lg text-sm cursor-pointer"
              >
                <a href="/jasa-cetak" className="flex items-center justify-center">
                  Lihat Jasa Cetak
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}
